import { useMemo } from 'react';

import type {
	CommentLocation,
	CommentReply,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import { expValEquals } from '@confluence/feature-experiments';

const createInlineMarkerMatcher =
	(parentLocation: CommentLocation) =>
	(reply: CommentReply | null): boolean => {
		if (!reply) {
			return false;
		}

		if (!expValEquals('confluence_frontend_replytoggler_fix', 'isEnabled', true)) {
			return true;
		}

		const replyLocation = reply.location as CommentLocation;

		return parentLocation.inlineMarkerRef === replyLocation.inlineMarkerRef;
	};

export const useIsEditorExpanded = (
	parentComment: CommentData,
	location: LocationValue,
): boolean => {
	const { replyToCommentId, location: commentLocation } = useCommentsPanelState();

	return useMemo(() => {
		if (location === LocationValue.FOOTER) {
			if (commentLocation === LocationValue.FOOTER) {
				return parentComment.id === replyToCommentId;
			}

			return false;
		}

		const inlineMarkerMatcher = createInlineMarkerMatcher(
			parentComment.location as CommentLocation,
		);

		const parentReplies = parentComment.replies || [];
		const matchedReplies: CommentReply[] = parentReplies.filter(inlineMarkerMatcher);

		if (commentLocation === LocationValue.PANEL) {
			const isReplyingToParent = replyToCommentId === parentComment.id;
			const isReplyingToChild = matchedReplies.some(
				(reply: CommentReply) => reply.id === replyToCommentId,
			);

			const canExpandForChildReply =
				isReplyingToChild &&
				expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true);

			return isReplyingToParent || canExpandForChildReply;
		}

		return false;
	}, [
		commentLocation,
		location,
		parentComment.id,
		parentComment.location,
		parentComment.replies,
		replyToCommentId,
	]);
};
